import { useEffect, useState } from 'react';
import type { Bridge } from '../../contracts/bridge';
import { RiskTier, RISK_TIER_LABELS } from '../../contracts/risk';
import { VEHICLE_CLASS_LABELS, VehicleClass } from '../../contracts/mitigation';
import { apiClient } from '../../core/apiClient';

interface RiskPanelProps {
  bridge: Bridge;
  currentRisk: RiskTier | null;
}

interface RiskResponse {
  bridge_id: string;
  bridge_name: string;
  deck_temp_c: number;
  surface_state: string;
  ice_thickness_mm: number;
  friction_estimate: number;
  risk_tier: string;
  vulnerable_vehicles: string[];
}

export function RiskPanel({ bridge }: RiskPanelProps) {
  const [risk, setRisk] = useState<RiskResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadRisk();
  }, [bridge.bridge_id]);

  const loadRisk = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get<RiskResponse>(
        `/engine/risk/${bridge.bridge_id}`
      );
      setRisk(response);
    } catch (err) {
      console.error('Failed to load risk:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getRiskBadgeClass = (tier: string) => {
    return `risk-badge risk-badge--${tier}`;
  };

  return (
    <div>
      <div className="panel-header">
        <h3 className="panel-title">Risk Assessment</h3>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center p-4">
          <div className="loading-spinner" />
        </div>
      )}

      {risk && !isLoading && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <span className={getRiskBadgeClass(risk.risk_tier)}>
              {RISK_TIER_LABELS[risk.risk_tier as RiskTier] || risk.risk_tier}
            </span>
            <span className="text-sm text-secondary">
              Friction: {risk.friction_estimate.toFixed(2)}
            </span>
          </div>

          <div className="card">
            <div className="text-sm text-secondary mb-1">Current Conditions</div>
            <div className="flex items-center gap-2">
              <div
                className={`surface-indicator surface-indicator--${risk.surface_state}`}
              />
              <span className="capitalize">{risk.surface_state}</span>
              <span className="text-secondary">
                • {risk.deck_temp_c.toFixed(1)}°C
              </span>
            </div>
          </div>

          {risk.vulnerable_vehicles.length > 0 && (
            <div className="card">
              <div className="text-sm text-secondary mb-2">Vulnerable Vehicles</div>
              <div className="flex flex-col gap-1">
                {risk.vulnerable_vehicles.map((v) => (
                  <div key={v} className="text-sm">
                    {VEHICLE_CLASS_LABELS[v as VehicleClass] || v}
                  </div>
                ))}
              </div>
            </div>
          )}

          {risk.ice_thickness_mm > 0 && (
            <div className="card">
              <div className="text-sm text-secondary mb-1">Ice Thickness</div>
              <div className="text-xl font-semibold">
                {risk.ice_thickness_mm.toFixed(1)} mm
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
